"use client"

import { UserAgreementDialog, UserAuthForm } from "@/components/user-auth-form"
import {
  isLoginExpiredPayload,
  LOGIN_EXPIRED_STORAGE_KEY,
} from "@/lib/loginExpiredStorage"
import { useEffect, useState, Suspense } from "react"
import toast from "react-hot-toast"
import Loading from "@/components/Loading"
import { doAuthorizeLogin } from "@/components/site/WeChatLogin"
import { WeChatQRCodeLogin } from "@/components/site/WeChatQRCodeLogin"
// import { WeChatQRCodeLogin } from "@/components/site/WeChatLogin"
import { Button } from "@/components/fun-ui/Button"
import { PhoneIcon, WechatIcon } from "../fun-ui/icons"
import { useStore } from "@/stores/appStore"
import BindMobileDialog from "./BindMobileDialog"
import { useParams, useRouter, useSearchParams } from "next/navigation"
import { cn } from "@/lib/utils"
import { AUTHORIZE_APPID, QRCONNECT_APPID } from "@/config/const"
import { useTrace } from "@/hooks/useTrace"

type SiteLoginProps = {
  code: string;
  className?: string;
}

function SiteLoginContent({
  code,
  className,
}: SiteLoginProps) {
  const [isWxBrowser, setIsWxBrowser] = useState(false);

  useEffect(() => {
    setIsWxBrowser(isWx());
  }, []);

  useEffect(() => {
    let raw: string | null = null;
    try {
      raw = sessionStorage.getItem(LOGIN_EXPIRED_STORAGE_KEY);
      // 进入登录页即清理，避免刷新页面再次读到同一条 payload 重复 toast
      sessionStorage.removeItem(LOGIN_EXPIRED_STORAGE_KEY);
      if (raw === null) return;
      const data = JSON.parse(raw) as unknown;
      if (!isLoginExpiredPayload(data)) return;
      toast.error(data.message);
    } catch {
      try {
        sessionStorage.removeItem(LOGIN_EXPIRED_STORAGE_KEY);
      } catch {
        /* 忽略 */
      }
    }
  }, []);

  // 0 'phone' 手机号登录
  // 1 'wechat' 微信授权登录
  // 2 'qrcode' 微信扫码登录
  const [loginMethod, setLoginMethod] = useState(
    !code ? 2 : (isWxBrowser ? 1 : 2)
  );
  const [open, setOpen] = useState(false);
  const [bindMobileOpen, setBindMobileOpen] = useState(false);
  const router = useRouter();
  const search = useSearchParams();
  const { inviteCode = '' } = useParams() as { inviteCode: string; };
  const callbackUrl = decodeURIComponent(search.get('callback') || '');
  const debug = !!search.get('debug');

  const { error: traceError } = useTrace();

  const {
    loginLoading,
    checkWechatLogin,
  } = useStore((state) => ({
    user: state.user,
    loginLoading: state.loginLoading,
    checkWechatLogin: state.checkWechatLogin,
  }));

  useEffect(() => {
    if (code) {
      const isWxBrowser = isWx();
      // 微信浏览器用户直接微信授权，非微信浏览器用户，扫码登录。
      setLoginMethod(isWxBrowser ? 1 : 2);
      (async () => {
        const appid = isWxBrowser ? AUTHORIZE_APPID : QRCONNECT_APPID;
        const user = await checkWechatLogin(code, appid);
        if (!user?.mobile) {
          setBindMobileOpen(true);
        } else if (user?.userId) {
          const toUrl = inviteCode ? '/papers?from=referrer' : '/papers';
          router.push(callbackUrl ? callbackUrl : toUrl);
        } else {
          traceError({
            code,
            appid,
            user,
          });
        }
      })();
    }
  }, [code, checkWechatLogin, router, callbackUrl, inviteCode, traceError]);

  const isPhoneLogin = loginMethod === 0 || loginMethod === 1;

  return (
    <div className={cn(
      "relative flex w-full md:w-[30.75rem] md:flex-none flex-col justify-center items-center mx-0 md:mx-auto px-5 md:px-14 bg-background/65 backdrop-blur rounded-[0.875rem] pt-5 pb-4 md:pt-12 md:pb-10 xl:pt-12 xl:pb-10 overflow-hidden",
      className,
    )}>
      <h1 className="text-[1.75rem] md:text-4xl leading-loose font-semibold tracking-tight">
        登录 Findin 飞引
      </h1>
      {/* <Suspense fallback={<Loading />}> */}
      <div className="flex w-full md:w-[23.75rem] min-h-[16.875rem] md:min-h-[20rem] items-center justify-center">
        {
          isPhoneLogin ?
          <UserAuthForm
            className="w-full min-w-0 py-3 md:py-7"
          /> :
          <WeChatQRCodeLogin />
        }
      </div>
      {/* </Suspense> */}
      <UserAgreementDialog
        open={open}
        setOpen={setOpen}
        onCancel={() => {
          setOpen(false);
        }}
        onConfirm={() => {
          setOpen(true);
        }}
      />
      <div className={cn(
        "flex flex-col w-full items-center space-y-3 md:space-y-4",
        !isWxBrowser && "max-md:hidden",
      )}>
        <div className="relative w-full px-1">
          <div className={cn(
            "w-full flex items-center",
            "before:mr-3  before:h-[0.063rem] before:flex-1 before:bg-border",
            "after:ml-3 after:h-[0.063rem] after:flex-1 after:bg-border",
          )}>
            <span className="text-annotation-foreground text-xs md:text-sm">或</span>
          </div>
        </div>
        <Button
          size="xs"
          className="border border-annotation-foreground hover:border-border bg-background hover:bg-muted text-muted-foreground rounded-full min-w-[120px] text-xs space-x-2"
          onClick={() => {
            if (isPhoneLogin) {
              // 微信内，直接授权登录
              if (isWxBrowser) {
                doAuthorizeLogin(search);
              } else {
                setLoginMethod(2);
              }
            } else {
              setLoginMethod(0);
            }
          }}
        >
          {
            isPhoneLogin ? (
              <><WechatIcon size={14} /><span>微信登录</span></>
            ) : (
              <><PhoneIcon size={14} /><span>手机号登录</span></>
            )
          }
        </Button>
      </div>
      {
        loginLoading ? <Loading size="lg" mask /> : null
      }
      <BindMobileDialog
        open={bindMobileOpen}
        onOpenChange={setBindMobileOpen}
      />
    </div>
  )
}

export function SiteLogin({
  code,
  className,
}: SiteLoginProps) {
  return (
    <Suspense fallback={<Loading mask />}>
      <SiteLoginContent code={code} className={className} />
    </Suspense>
  );
}

export const isWx = () => {
  const userAgent = window.navigator.userAgent.toLowerCase();
  return userAgent.includes('micromessenger');
}
